import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Drawer from "@mui/material/Drawer";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import Container from "@mui/material/Container";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LoginIcon from "@mui/icons-material/Login";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ForumIcon from "@mui/icons-material/Forum";
import io from "socket.io-client";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createAxios } from "../../../createAxios";
import { findUser, getNotifications, logout } from "../../../redux/apiRequest";
import { Badge, Chip, ClickAwayListener, Paper, Popper } from "@mui/material";
import { getUserId } from "../../../utils/authSession";

const socket = io.connect(
  process.env.REACT_APP_SOCKET_URL || "https://lingualica.dukelewis.com"
);

const navItemSx = {
  my: 1,
  mx: 0.25,
  color: "var(--toeic-ink)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.45,
  whiteSpace: "nowrap",
  px: 1.55,
  borderRadius: "999px",
  fontWeight: 700,
  position: "relative",
  overflow: "visible",
  isolation: "isolate",
  transition: "color 180ms ease, background-color 180ms ease, transform 180ms var(--toeic-ease-fluid)",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: "-1px",
    borderRadius: "inherit",
    border: "2px solid var(--toeic-sky)",
    boxShadow: "0 0 0 3px rgba(70, 168, 232, 0.08)",
    pointerEvents: "none",
    opacity: 0,
    transform: "scaleX(0.08)",
    transformOrigin: "left center",
    transition:
      "opacity 120ms ease, transform 360ms cubic-bezier(.32,.72,0,1)",
  },
  "&:hover": {
    color: "var(--toeic-sky-deep)",
    bgcolor: "rgba(232, 246, 255, 0.86)",
    transform: "translateY(-1px)",
  },
  "&:hover::before": {
    opacity: 1,
    transform: "scaleX(1)",
  },
  "& .MuiButton-endIcon": {
    marginLeft: 0.2,
    marginRight: 0,
  },
};

const softMenuPaperSx = {
  mt: 1.25,
  minWidth: 180,
  borderRadius: "16px",
  border: "1px solid rgba(185, 220, 255, 0.72)",
  bgcolor: "rgba(255, 254, 253, 0.94)",
  boxShadow: "var(--toeic-shadow-hover)",
  backdropFilter: "blur(14px)",
  overflow: "hidden",
  "& .MuiList-root": {
    p: 0.75,
  },
};

const softMenuItemSx = {
  borderRadius: "12px",
  px: 1.25,
  py: 1,
  color: "var(--toeic-ink)",
  fontWeight: 600,
  "&:hover": {
    bgcolor: "#e8f6ff",
    color: "var(--toeic-sky-deep)",
  },
};

const authMenuPaperSx = {
  ...softMenuPaperSx,
  minWidth: 292,
  borderRadius: "20px",
  "& .MuiList-root": {
    p: 0,
  },
};

const menuTitleTextSx = {
  color: "var(--toeic-ink)",
  fontWeight: 600,
  lineHeight: 1.25,
};

const menuCaptionTextSx = {
  mt: 0.35,
  color: "var(--toeic-ink-soft)",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: 1.35,
};

const authMenuHeaderSx = {
  p: 1.75,
  bgcolor: "var(--toeic-paper-grid)",
  borderBottom: "1px solid rgba(185, 220, 255, 0.72)",
};

const authMenuIconSx = {
  width: 34,
  height: 34,
  mr: 1.15,
  borderRadius: "11px",
  display: "grid",
  placeItems: "center",
  flex: "0 0 auto",
};

const authSignupLinkSx = {
  px: 2,
  pb: 1.5,
  color: "var(--toeic-ink-soft)",
  fontSize: 13,
  fontWeight: 400,
  textAlign: "center",
};

const authMenuItemSx = {
  mx: 1,
  mb: 1,
  p: 1.25,
  borderRadius: "14px",
  alignItems: "flex-start",
  color: "var(--toeic-ink)",
  "&:hover": {
    bgcolor: "#e8f6ff",
  },
};

function Header() {
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const [anchorElNavGroup, setAnchorElNavGroup] = React.useState(null);
  const [activeNavGroup, setActiveNavGroup] = React.useState(null);
  const [anchorElAdmin, setAnchorElAdmin] = React.useState(null);
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const currentUser = useSelector((state) => state.user.signin.currentUser);
  const isAdmin = currentUser?.metadata?.user?.isAdmin;
  const avatar = currentUser?.metadata?.user?.user_avatar;
  const accessToken = currentUser?.metadata.tokens.accessToken;
  const userID = getUserId(currentUser);
  const dispatch = useDispatch();
  const axiosJWT = createAxios(currentUser);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout(accessToken, userID, dispatch, navigate, axiosJWT);
  };
  const navGroups = [
    {
      title: "Learn",
      items: [
        { title: "Flashcards", href: "/flashcards", caption: "Vocabulary review" },
        { title: "IELTS", href: "/ielts", caption: "Exam preparation" },
        { title: "Resources", href: "/document", caption: "Learning documents" },
        { title: "Guide", href: "/guide", caption: "How to practice" },
      ],
    },
    {
      title: "Practice",
      items: [
        { title: "TOEIC Tests", href: "/test", caption: "Answer sheets" },
        { title: "Ranking", href: "/ranking", caption: "Leaderboard" },
      ],
    },
    {
      title: "Progress",
      items: [
        { title: "History", href: "/history", caption: "Past attempts" },
        { title: "Ranking", href: "/ranking", caption: "Score comparison" },
      ],
    },
    ...(isAdmin
      ? [
          {
            title: "Admin",
            items: [
              { title: "Dashboard", href: "/management", caption: "Manage content" },
              { title: "TOEIC Tests", href: "/management/test", caption: "Answer sheets" },
              { title: "Resources", href: "/management/document", caption: "Documents" },
              { title: "Collections", href: "/management/collection", caption: "Homepage sliders" },
              { title: "Users", href: "/management/user", caption: "Accounts" },
            ],
          },
        ]
      : []),
  ];
  const settings = [
    { title: "Profile", href: `/profile/${userID}` },
    { title: "Account", href: `/account/${userID}` },
    { title: "Logout", href: "", onClick: handleLogout },
  ];
  const admins = [
    { title: "Resource", href: "/document/add" },
    { title: "Slider", href: "/slider/add" },
    { title: "TOEIC Test", href: "/test/add" },
  ];
  const activeNavItems =
    navGroups.find((group) => group.title === activeNavGroup)?.items || [];

  const handleOpenNavMenu = () => {
    setMobileNavOpen(true);
  };
  const handleOpenNavGroup = (event, groupTitle) => {
    setActiveNavGroup(groupTitle);
    setAnchorElNavGroup(event.currentTarget);
  };
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };
  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleOpenAdminMenu = (event) => {
    setAnchorElAdmin(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setMobileNavOpen(false);
  };
  const handleCloseNavGroup = () => {
    setAnchorElNavGroup(null);
    setActiveNavGroup(null);
  };
  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };
  const handleCloseAdminMenu = () => {
    setAnchorElAdmin(null);
  };

  const [notifications, setNotifications] = React.useState([]);
  const [haveNotRead, setHaveNotRead] = React.useState(0);
  const fetchData = async () => {
    try {
      const notiData = await getNotifications(userID, dispatch);
      let count = 0;

      const userPromises = notiData.map(async (notification) => {
        if (!notification.isRead) {
          count += 1;
        }
        const senderData = await findUser(
          notification.notification_sender_id,
          dispatch
        );
        const receiverData = await findUser(
          notification.notification_receiver_id,
          dispatch
        );

        return {
          ...notification,
          senderData: senderData.metadata.user,
          receiverData: receiverData.metadata.user,
        };
      });

      const notificationsWithUserData = await Promise.all(userPromises);
      setNotifications(notificationsWithUserData);
      setHaveNotRead(count);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const [anchorElNotifications, setAnchorElNotifications] =
    React.useState(null);
  const [openNotification, setOpenNotification] = React.useState(false);

  const handleOpenNotifications = (event) => {
    setAnchorElNotifications(event.currentTarget);
    setOpenNotification((prev) => !prev);
  };

  const handleCloseNotifications = () => {
    setOpenNotification(false);
  };

  const handleNotificationClick = (notification) => {
    navigate(`/answersheet/${notification.notification_answer_id}`);
    handleCloseNotifications();
  };

  const getUserDisplayName = (user) =>
    user?.name || user?.user_name || "Unknown user";

  const getUserAvatar = (user) => user?.avatar || user?.user_avatar || "";

  React.useEffect(() => {
    fetchData();
    socket.emit("join_room", userID);

    socket.on("receive_notification", (data) => {
      fetchData();
    });

    return () => {
      socket.off("receive_notification");
    };
  }, [socket]);

  React.useEffect(() => {
    const updateHeaderState = () => {
      setIsScrolled(window.scrollY > 12);
    };

    updateHeaderState();
    window.addEventListener("scroll", updateHeaderState, { passive: true });

    return () => {
      window.removeEventListener("scroll", updateHeaderState);
    };
  }, []);

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        bgcolor: "transparent",
        color: "var(--toeic-ink)",
        px: { xs: 2, md: 3 },
        pt: isScrolled ? 1.5 : 1.25,
        boxSizing: "border-box",
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          width: "100%",
          maxWidth: "var(--default-layout-width)",
          px: 0,
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "auto minmax(0, 1fr) auto",
              md: "minmax(210px, 1fr) auto minmax(210px, 1fr)",
            },
            columnGap: { xs: 0.5, md: 0 },
            alignItems: "center",
            minHeight: isScrolled ? "56px !important" : "72px !important",
            px: isScrolled ? { xs: 1.5, md: 2 } : 0,
            py: isScrolled ? 0.75 : 0,
            border: 0,
            borderRadius: isScrolled ? "999px" : 0,
            bgcolor: isScrolled ? "rgba(255, 254, 253, 0.92)" : "transparent",
            boxShadow: isScrolled
              ? "0 10px 30px rgba(19, 43, 63, 0.12)"
              : "none",
            backdropFilter: isScrolled ? "blur(12px)" : "none",
            transition:
              "min-height 180ms ease, padding 180ms ease, background-color 180ms ease, border-radius 180ms ease, box-shadow 180ms ease, backdrop-filter 180ms ease",
          }}
        >
          <Box
            component={Link}
            to="/"
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              gap: 1,
              justifySelf: "start",
              textDecoration: "none",
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                display: "grid",
                placeItems: "center",
                flex: "0 0 auto",
                borderRadius: isScrolled ? "10px" : 0,
                bgcolor: isScrolled ? "var(--toeic-paper-grid)" : "transparent",
                boxShadow: isScrolled ? "var(--toeic-shadow-card)" : "none",
                overflow: "hidden",
              }}
            >
              <Box
                component="img"
                src="/logo-removebg.png"
                alt="Lingualica"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </Box>
            <Typography
              variant="h6"
              noWrap
              sx={{
                fontWeight: 700,
                color: "var(--toeic-ink)",
                letterSpacing: 0,
              }}
            >
              Lingualica
            </Typography>
          </Box>
          {/*
          <Typography
            variant="h6"
            noWrap
            component="a"
            href="/"
            sx={{
              mr: 2,
              display: { xs: "none", md: "flex" },
              fontFamily: "monospace",
              fontWeight: 700,
              letterSpacing: ".3rem",
              color: "inherit",
              textDecoration: "none",
            }}
          >
            HOME
          </Typography>
          */}

          <Box sx={{ justifySelf: "start", display: { xs: "flex", md: "none" } }}>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              sx={{
                color: "var(--toeic-sky-deep)",
                bgcolor: "transparent",
                "&:hover": { bgcolor: "#e8f6ff" },
              }}
            >
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="left"
              open={mobileNavOpen}
              onClose={handleCloseNavMenu}
              sx={{ display: { xs: "block", md: "none" } }}
              PaperProps={{
                className: "lingualica-scrollarea",
                sx: {
                  width: "min(84vw, 340px)",
                  border: 0,
                  borderRadius: 0,
                  bgcolor: "rgba(255, 254, 253, 0.97)",
                  boxShadow: "24px 0 52px -34px rgba(19, 43, 63, 0.6)",
                  backdropFilter: "blur(14px)",
                  display: "flex",
                  flexDirection: "column",
                  height: "100dvh",
                  maxHeight: "100dvh",
                  overflow: "hidden",
                },
              }}
            >
              <Box sx={{ p: 2.25, borderBottom: "1px solid rgba(185, 220, 255, 0.72)" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    component="img"
                    src="/logo-removebg.png"
                    alt="Lingualica"
                    sx={{ width: 36, height: 36, objectFit: "contain" }}
                  />
                  <Typography sx={{ color: "var(--toeic-ink)", fontWeight: 800, fontSize: 24 }}>
                    Lingualica
                  </Typography>
                </Box>
              </Box>
              <Box
                className="lingualica-scrollarea"
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                  p: 1.25,
                }}
              >
                {navGroups.map((group) => (
                  <Box key={group.title} sx={{ mb: 1.25 }}>
                    <Typography sx={{ px: 1.25, py: 0.75, color: "var(--toeic-sky-deep)", fontSize: 12, fontWeight: 800, textTransform: "uppercase" }}>
                      {group.title}
                    </Typography>
                    {group.items.map((item) => (
                      <Link
                        key={`${group.title}-${item.title}`}
                        to={item.href}
                        style={{ textDecoration: "none", color: "inherit" }}
                      >
                        <MenuItem onClick={handleCloseNavMenu} sx={{ ...softMenuItemSx, alignItems: "flex-start", py: 1.2, mb: 0.35 }}>
                          <Box>
                            <Typography sx={{ color: "var(--toeic-ink)", fontWeight: 700, fontSize: 17 }}>
                              {item.title}
                            </Typography>
                            <Typography sx={{ color: "var(--toeic-ink-soft)", fontSize: 13, fontWeight: 500 }}>
                              {item.caption}
                            </Typography>
                          </Box>
                        </MenuItem>
                      </Link>
                    ))}
                  </Box>
                ))}
              </Box>
            </Drawer>
          </Box>
          <Box
            component={Link}
            to="/"
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              gap: 0.75,
              minWidth: 0,
              overflow: "hidden",
              textDecoration: "none",
              justifySelf: "start",
            }}
          >
            <Box
              component="img"
              src="/logo-removebg.png"
              alt="Lingualica"
              sx={{
                width: { xs: 30, sm: 34 },
                height: { xs: 30, sm: 34 },
                objectFit: "contain",
                flex: "0 0 auto",
              }}
            />
            <Typography
              variant="h5"
              noWrap
              sx={{
                minWidth: 0,
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontWeight: 800,
                letterSpacing: 0,
                color: "var(--toeic-ink)",
                textDecoration: "none",
                fontSize: { xs: 26, sm: 32 },
                lineHeight: 1.05,
              }}
            >
              Lingualica
            </Typography>
          </Box>
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              justifySelf: "center",
              alignItems: "center",
              gap: 0.35,
              px: isScrolled ? 0 : 0.75,
              py: 0.35,
              border: "none",
              borderRadius: 0,
              bgcolor: "transparent",
            }}
          >
            {navGroups.map((group) => (
              <Button
                key={group.title}
                onClick={(event) => handleOpenNavGroup(event, group.title)}
                endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
                sx={navItemSx}
              >
                {group.title}
              </Button>
            ))}
            <Menu
              anchorEl={anchorElNavGroup}
              open={Boolean(anchorElNavGroup)}
              onClose={handleCloseNavGroup}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
              PaperProps={{ sx: { ...softMenuPaperSx, minWidth: 240 } }}
            >
              {activeNavItems.map((item) => (
                <Link
                  key={`${activeNavGroup}-${item.title}`}
                  to={item.href}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <MenuItem onClick={handleCloseNavGroup} sx={{ ...softMenuItemSx, alignItems: "flex-start" }}>
                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>{item.title}</Typography>
                      <Typography sx={{ color: "var(--toeic-ink-soft)", fontSize: 12, lineHeight: 1.35 }}>
                        {item.caption}
                      </Typography>
                    </Box>
                  </MenuItem>
                </Link>
              ))}
            </Menu>
          </Box>

          <Box
            sx={{
              justifySelf: { md: "end" },
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: { xs: 0.35, md: 0.75 },
              flexGrow: { xs: 0, md: 0 },
              minWidth: 0,
            }}
          >
          {isAdmin ? (
            <Box sx={{ justifySelf: "end", display: "flex", alignItems: "center", flexGrow: 0 }}>
              <Tooltip onClick={handleOpenAdminMenu} title="Create new test">
                <IconButton sx={{ p: 0, width: { xs: 36, md: 40 }, height: { xs: 36, md: 40 } }}>
                  <AddIcon
                    sx={{
                      color: "var(--toeic-sky-deep)",
                      fontSize: { xs: 28, md: 32 },
                      marginRight: { xs: 0, md: "12px" },
                    }}
                  />
                </IconButton>
              </Tooltip>
              <Menu
                sx={{ mt: "45px" }}
                id="menu-appbar"
                anchorEl={anchorElAdmin}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(anchorElAdmin)}
                onClose={handleCloseAdminMenu}
                PaperProps={{ sx: softMenuPaperSx }}
              >
                {admins.map((admin) => (
                  <Link
                    key={admin.title}
                    to={admin.href}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <MenuItem onClick={handleCloseAdminMenu} sx={softMenuItemSx}>
                      <Typography sx={{ fontWeight: 700 }}>{admin.title}</Typography>
                    </MenuItem>
                  </Link>
                ))}
              </Menu>
            </Box>
          ) : (
            ""
          )}
          {!currentUser ? (
            <Box sx={{ justifySelf: "end", display: "flex", alignItems: "center", flexGrow: 0 }}>
              <Tooltip title="Open options">
                <Button
                  onClick={handleOpenMenu}
                  variant="text"
                  startIcon={<LoginIcon />}
                  sx={{
                    minHeight: 36,
                    px: 1.75,
                    borderRadius: "999px",
                    bgcolor: isScrolled ? "rgba(255, 254, 253, 0.82)" : "#e8f6ff",
                    color: "var(--toeic-ink)",
                    fontWeight: 700,
                    boxShadow: isScrolled
                      ? "0 8px 20px rgba(19, 43, 63, 0.1)"
                      : "none",
                    backdropFilter: isScrolled ? "blur(10px)" : "none",
                    "& .MuiButton-startIcon": { mr: 0.75 },
                    "&:hover": {
                      bgcolor: "#e8f6ff",
                      color: "var(--toeic-sky-deep)",
                    },
                  }}
                >
                  Sign in
                </Button>
              </Tooltip>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                PaperProps={{ sx: authMenuPaperSx }}
              >
                <Box sx={authMenuHeaderSx}>
                  <Typography sx={{ ...menuTitleTextSx, fontSize: 16 }}>
                    Sign in to Lingualica
                  </Typography>
                </Box>
                <Box sx={{ py: 1 }}>
                  <Link to="/signin" style={{ textDecoration: "none", color: "inherit" }}>
                    <MenuItem onClick={handleCloseMenu} sx={{ ...authMenuItemSx, bgcolor: "#f3fbff" }}>
                      <Box
                        sx={{
                          ...authMenuIconSx,
                          bgcolor: "#e8f6ff",
                          color: "var(--toeic-sky-deep)",
                        }}
                      >
                        <LoginIcon fontSize="small" />
                      </Box>
                      <Box>
                        <Typography sx={menuTitleTextSx}>Sign in</Typography>
                        <Typography sx={menuCaptionTextSx}>Continue your learning.</Typography>
                      </Box>
                    </MenuItem>
                  </Link>
                  <Typography sx={authSignupLinkSx}>
                    New here?{" "}
                    <Box
                      component={Link}
                      to="/signup"
                      onClick={handleCloseMenu}
                      sx={{
                        color: "var(--toeic-sky-deep)",
                        fontWeight: 600,
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      Create account
                    </Box>
                  </Typography>
                </Box>
              </Menu>
            </Box>
          ) : (
            <Box sx={{ justifySelf: "end", display: "flex", alignItems: "center", flexGrow: 0 }}>
              {/* ______________________________ */}
              <Tooltip title="Notifications">
                <IconButton
                  onClick={handleOpenNotifications}
                  sx={{
                    mr: { xs: 0.25, md: 1.5 },
                    width: { xs: 36, md: 40 },
                    height: { xs: 36, md: 40 },
                    bgcolor: openNotification ? "#e8f6ff" : "transparent",
                    color: "var(--toeic-sky-deep)",
                    "&:hover": { bgcolor: "#e8f6ff" },
                  }}
                >
                  <Badge
                    badgeContent={haveNotRead}
                    color="secondary"
                    sx={{
                      "& .MuiBadge-badge": {
                        color: "#ffffff",
                        fontWeight: 700,
                        minWidth: 18,
                        height: 18,
                        fontSize: 11,
                      },
                    }}
                  >
                    <NotificationsIcon sx={{ fontSize: { xs: 24, md: 28 } }} />
                  </Badge>
                </IconButton>
              </Tooltip>
              <Popper
                open={openNotification}
                anchorEl={anchorElNotifications}
                placement="bottom-end"
                modifiers={[
                  { name: "offset", options: { offset: [0, 10] } },
                  { name: "preventOverflow", options: { padding: 12 } },
                ]}
                sx={{ zIndex: 1400, maxWidth: "100vw" }}
              >
                <ClickAwayListener onClickAway={handleCloseNotifications}>
                  <Paper
                    elevation={0}
                    sx={{
                      width: { xs: "calc(100vw - 24px)", sm: 380 },
                      maxWidth: "calc(100vw - 24px)",
                      borderRadius: { xs: "14px", sm: "18px" },
                      border: "1px solid rgba(185, 220, 255, 0.72)",
                      boxShadow: "var(--toeic-shadow-hover)",
                      overflow: "hidden",
                      bgcolor: "var(--toeic-surface)",
                      backdropFilter: "blur(14px)",
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        py: { xs: 1.5, sm: 2 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1.5,
                        bgcolor: "var(--toeic-paper-grid)",
                      }}
                    >
                      <Box>
                        <Typography sx={{ color: "var(--toeic-ink)", fontWeight: 700 }}>
                          Notifications
                        </Typography>
                        <Typography sx={{ color: "var(--toeic-ink-soft)", fontSize: 13 }}>
                          Replies and activity from your learning sessions.
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={`${haveNotRead} unread`}
                        sx={{
                          bgcolor: haveNotRead ? "#e6f8f2" : "#e8f6ff",
                          color: haveNotRead ? "#27866d" : "var(--toeic-sky-deep)",
                          flex: "0 0 auto",
                        }}
                      />
                    </Box>
                    <Box aria-hidden sx={{ borderTop: "1.5px dotted var(--toeic-line)" }} />
                    <Box
                      sx={{
                        p: 1.25,
                        maxHeight: 360,
                        overflowY: "auto",
                        bgcolor: "var(--toeic-surface)",
                      }}
                    >
                      {notifications.length === 0 ? (
                        <Box
                          sx={{
                            p: 3,
                            textAlign: "center",
                            borderRadius: "14px",
                            border: "1.5px dashed var(--toeic-line)",
                            bgcolor: "var(--toeic-paper-grid)",
                          }}
                        >
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              mx: "auto",
                              mb: 1,
                              borderRadius: "14px",
                              display: "grid",
                              placeItems: "center",
                              bgcolor: "#e8f6ff",
                              color: "var(--toeic-sky-deep)",
                            }}
                          >
                            <ForumIcon />
                          </Box>
                          <Typography sx={{ color: "var(--toeic-ink)", fontWeight: 700 }}>
                            No notifications
                          </Typography>
                          <Typography sx={{ color: "var(--toeic-ink-soft)", fontSize: 13 }}>
                            New replies will show up here.
                          </Typography>
                        </Box>
                      ) : (
                        notifications.map((notification) => {
                          const senderName = getUserDisplayName(notification.senderData);
                          const receiverName = getUserDisplayName(notification.receiverData);
                          const senderAvatar = getUserAvatar(notification.senderData);

                          return (
                            <Box
                              key={notification._id}
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "44px 1fr",
                                gap: 1.25,
                                alignItems: "start",
                                mb: 1,
                                cursor: "pointer",
                                border: notification.isRead
                                  ? "1px solid var(--toeic-line)"
                                  : "1px solid rgba(79, 191, 159, 0.48)",
                                borderRadius: "14px",
                                p: 1.25,
                                bgcolor: notification.isRead ? "var(--toeic-surface)" : "#eefbf7",
                                transition:
                                  "border-color 0.2s ease, background-color 0.2s ease, transform 0.2s ease",
                                "&:hover": {
                                  borderColor: "var(--toeic-sky)",
                                  bgcolor: "var(--toeic-paper)",
                                  transform: "translateY(-1px)",
                                },
                              }}
                              onClick={() => handleNotificationClick(notification)}
                            >
                              <Avatar
                                src={senderAvatar}
                                alt={senderName}
                                sx={{
                                  width: 44,
                                  height: 44,
                                  bgcolor: "#e8f6ff",
                                  color: "var(--toeic-sky-deep)",
                                  fontWeight: 700,
                                }}
                              >
                                {senderName.charAt(0).toUpperCase()}
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1,
                                    mb: 0.25,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      color: "var(--toeic-ink)",
                                      fontWeight: 700,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {senderName}
                                  </Typography>
                                  {!notification.isRead && (
                                    <Box
                                      sx={{
                                        width: 9,
                                        height: 9,
                                        borderRadius: "50%",
                                        bgcolor: "#35c759",
                                        flex: "0 0 auto",
                                      }}
                                    />
                                  )}
                                </Box>
                                <Typography
                                  sx={{
                                    color: "var(--toeic-ink-soft)",
                                    fontSize: 13,
                                    lineHeight: 1.45,
                                  }}
                                >
                                  replied to <strong>{receiverName}</strong>
                                </Typography>
                                <Typography
                                  sx={{
                                    mt: 0.75,
                                    px: 1,
                                    py: 0.75,
                                    borderRadius: "12px",
                                    bgcolor: "var(--toeic-paper-grid)",
                                    color: "var(--toeic-ink)",
                                    fontWeight: 700,
                                    fontSize: 13,
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  }}
                                >
                                  "{notification.notification_content}"
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })
                      )}
                    </Box>
                  </Paper>
                </ClickAwayListener>
              </Popper>
              <Tooltip title="Open settings">
                <IconButton
                  onClick={handleOpenUserMenu}
                  sx={{
                    p: 0.25,
                    border: "2px solid var(--toeic-paper-grid)",
                    boxShadow: "var(--toeic-shadow-stamp)",
                  }}
                >
                  <Avatar
                    alt="Remy Sharp"
                    src={
                      avatar
                        ? avatar
                        : "https://i.pinimg.com/736x/49/c6/f1/49c6f19b6c8033d8a83e899d11719f07.jpg"
                    }
                    sx={{
                      width: { xs: 36, md: 40 },
                      height: { xs: 36, md: 40 },
                    }}
                  />
                </IconButton>
              </Tooltip>
              <Menu
                sx={{ mt: "45px" }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
                PaperProps={{ sx: softMenuPaperSx }}
              >
                {settings.map((setting) => (
                  <Link
                    key={setting.title}
                    to={setting.href}
                    style={{ textDecoration: "none", color: "inherit" }}
                    onClick={setting.onClick}
                  >
                    <MenuItem onClick={handleCloseUserMenu} sx={softMenuItemSx}>
                      <Typography sx={{ fontWeight: 700 }}>
                        {setting.title}
                      </Typography>
                    </MenuItem>
                  </Link>
                ))}
              </Menu>
            </Box>
          )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}
export default Header;

